import React, { memo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { BaseSelectField } from 'common/components';
import { usePaginationTable } from 'common/hooks';
import { pageSizeDelimiters } from 'common/constants/tables';
import { ArrowIcon } from 'common/icons';

const TablePagination = ({ dataCount, params, getList }) => {
  const {
    currentPage,
    currentPageParam,
    pageSize,
    pagesCount,
    handlePageSizeChange,
    handlePageChange,
    handleInputChange,
    handleInputBlur,
    handleInputKeyDown,
  } = usePaginationTable({ dataCount, params, getList });

  return (
    <div className="BOPaginationTable__pagination">
      <div className="BOPaginationTable__pagination__nav">
        <div className="BOPaginationTable__pagination__nav-buttons">
          <button
            type="button"
            disabled={currentPageParam <= 1}
            className={classNames(
              'BOPaginationTable__pagination__nav-btn',
              'BOPaginationTable__pagination__nav-btn--previous'
            )}
            onClick={() => handlePageChange('1')}>
            <ArrowIcon />
            <ArrowIcon />
          </button>
          <button
            type="button"
            disabled={currentPageParam <= 1}
            className={classNames(
              'BOPaginationTable__pagination__nav-btn',
              'BOPaginationTable__pagination__nav-btn--previous'
            )}
            onClick={() => handlePageChange(`${Number(currentPage) - 1}`)}>
            <ArrowIcon />
          </button>

          <button
            type="button"
            className={classNames(
              'BOPaginationTable__pagination__nav-btn',
              'BOPaginationTable__pagination__nav-btn--next'
            )}
            disabled={currentPageParam >= pagesCount}
            onClick={() => handlePageChange(`${Number(currentPage) + 1}`)}>
            <ArrowIcon />
          </button>
          <button
            type="button"
            className={classNames(
              'BOPaginationTable__pagination__nav-btn',
              'BOPaginationTable__pagination__nav-btn--next'
            )}
            disabled={currentPageParam >= pagesCount}
            onClick={() => handlePageChange(String(pagesCount))}>
            <ArrowIcon />
            <ArrowIcon />
          </button>
        </div>
        <div className="BOPaginationTable__pagination__nav-input-wrapper">
          <FormattedMessage id="justGoToPage" />
          <input
            className="BOPaginationTable__pagination__nav-input"
            value={currentPage}
            disabled={pagesCount === 1}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onKeyDown={handleInputKeyDown}
          />
        </div>
      </div>
      <div className="BOPaginationTable__pagination__info">
        <div className="BOPaginationTable__pagination__select-wrapper">
          <BaseSelectField
            name="pageSize"
            noLabel
            textId="justShow"
            getOptionLabel={({ label }) => `Show ${label}`}
            value={{ value: pageSize, label: pageSize }}
            options={pageSizeDelimiters.map(item => ({ value: item, label: item }))}
            onChange={option => handlePageSizeChange(option.value)}
          />
        </div>
        <div className="BOPaginationTable__pagination__current-page-info">
          <FormattedMessage id="justPage" />
          <FormattedMessage id="justOf">
            {txt => (
              <span className="BOPaginationTable__pagination__current-page-info--bold">
                {currentPageParam || 1} {txt} {pagesCount}
              </span>
            )}
          </FormattedMessage>
        </div>
      </div>
    </div>
  );
};

const MemoizedTablePagination = memo(TablePagination, (prev, next) => prev.dataCount === next.dataCount);

TablePagination.propTypes = {
  dataCount: PropTypes.number.isRequired,
  params: PropTypes.object.isRequired,
  getList: PropTypes.func.isRequired,
};

export default MemoizedTablePagination;
